import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ItemContext, ReactCalendarGroupRendererProps, TimelineItemBase } from "react-calendar-timeline";

import { useIsMutating } from "@tanstack/react-query";
import moment from "moment";
import { Moment } from "moment/moment";

import {
  TFunction,
  doRangesOverlap,
  timeIsWithinReportingPeriod,
  useAppType,
  useGetCarbonProgramOutcomes,
  useGetFarms,
  useGetFieldCropRecommendations,
  useGetFieldPractices,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizerApplications,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useLocation,
  useNavigate,
  useTranslation,
  useUpdateFieldCropPractice,
  useValidateCropPeriods,
} from "utils";

import {
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CategorizedFieldCropRecommendation,
  CropFCPO,
  CropItemData,
  CropPracticesRequestBody,
  FertilizerManagement,
  FieldDetails,
  FieldListField,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeFCPO,
  PracticeSlug,
  VIEW_TYPE,
} from "models";

import { CropRotationCard, Icon, PageSpin, TimelineCalendar, Message as message } from "components";

import { AddEditCropDrawer } from "pages/app/all-field-view/components/AddEditCropDrawer.tsx";
import { NEW_CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { useMissingDataNavigation } from "pages/app/all-field-view/hooks/useMissingDataNavigation";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { PracticeCard } from "pages/app/field-timeline/components/PracticeCard.tsx";
import { createCropItems } from "pages/app/field-timeline/utils/CropUtils.ts";
import { createFertilizerItems } from "pages/app/field-timeline/utils/FertilizerUtils.ts";
import { createPlaceholderItems } from "pages/app/field-timeline/utils/PlaceholderUtils.ts";
import { checkIfMandatoryPractice, getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils";
import { createRecommendations } from "pages/app/field-timeline/utils/RecommendationUtils.ts";
import { createCropPracticesRequestBody } from "pages/app/field-timeline/utils/RequestUtils.ts";
import { createSeedingItems } from "pages/app/field-timeline/utils/SeedingUtils.ts";
import { createTillageItems } from "pages/app/field-timeline/utils/TillageUtils.ts";
import { CustomTimelineGroup, createGroups, getGroupsToShow, isDateValidConsideringLastConfirmedCrop } from "pages/app/field-timeline/utils/TimelineUtils.ts";

type Id = number | string;
export type TillageValue = {
  name: string;
  startDate?: string;
  endDate: string;
  type: PracticeSlug;
  isBaseline: boolean;
  isConfirmed: boolean;
  isMandatory: boolean;
  payout?: number;
};
export type SeedingValue = {
  name: string;
  startDate?: string;
  endDate: string;
  seedingPracticeId: string | null;
  isBaseline: boolean;
  isConfirmed: boolean;
  isMandatory: boolean;
  payout?: number;
};
export type FertilizerValue = {
  name: string;
  startDate?: string;
  endDate: string;
  type: PracticeSlug;
  isBaseline: boolean;
  isConfirmed: boolean;
  isMandatory: boolean;
  payout?: number;
};
export type TillageItemData = {
  tillageValues: TillageValue[];
};
export type FertilizerItemData = {
  fertilizerValues: FertilizerValue[];
};
export type SeedingItemData = {
  seedingValue: SeedingValue;
};
export type TimelineCalendarItemsFieldDetailsProps<T extends CropItemData | TillageItemData | FertilizerItemData | SeedingItemData> = {
  practiceType: PRACTICE_TYPE;
  cropItemId?: string;
  isPlaceholder?: boolean;
  data: T;
};
export type TimelineCalendarItemsProps<T extends CropItemData | TillageItemData | FertilizerItemData | SeedingItemData> =
  TimelineCalendarItemsFieldDetailsProps<T> & TimelineItemBase<Moment>;

export enum GROUP_TYPE {
  MAIN_CROP = 0,
  MAIN_CROP_TILLAGE = 1,
  MAIN_CROP_SEEDING = 2,
  MAIN_CROP_FERTILIZER = 3,
  SIDE_CROP = 4,
  SIDE_CROP_TILLAGE,
  SIDE_CROP_SEEDING,
  SIDE_CROP_FERTILIZER,
  FILLER = 8, // this is to fill the remaining space in the timeline
}
export const PRACTICE_GROUPS = {
  mainCrop: [GROUP_TYPE.MAIN_CROP, GROUP_TYPE.MAIN_CROP_TILLAGE, GROUP_TYPE.MAIN_CROP_SEEDING, GROUP_TYPE.MAIN_CROP_FERTILIZER],
  sideCrop: [GROUP_TYPE.SIDE_CROP, GROUP_TYPE.SIDE_CROP_TILLAGE, GROUP_TYPE.SIDE_CROP_SEEDING, GROUP_TYPE.SIDE_CROP_FERTILIZER],
};

export enum PRACTICE_TYPE {
  MAIN_CROP = "MAIN_CROP",
  UNDERSOWING_CROP = "UNDERSOWING_CROP",
  COVER_CROP = "COVER_CROP",
  FERTILIZER = "FERTILIZER",
  TILLAGE = "TILLAGE",
  SEEDING = "SEEDING",
  UNKNOWN = "UNKNOWN",
  SIDE_CROP_RECOMMENDATION = "SIDE_CROP_RECOMMENDATION",
}
export enum UPDATE_TYPE {
  START = "START",
  END = "END",
  MOVE = "MOVE",
}

const dayInMilliseconds = 1 * 24 * 60 * 60 * 1000;

export const FieldDetailsTimeline = ({
  fieldDetails,
  showRecommendations,
  isEnrolled,
  carbonProgram,
  setHasMissingData,
  navigateToMissingData,
  setNavigateToMissingData,
  simulationId,
  reportingYear,
}: {
  fieldDetails: FieldDetails;
  showRecommendations: boolean;
  isEnrolled: boolean;
  carbonProgram: CarbonProgram | null;
  setHasMissingData: Dispatch<SetStateAction<boolean>>;
  navigateToMissingData: boolean;
  setNavigateToMissingData: Dispatch<SetStateAction<boolean>>;
  reportingYear: number;
  simulationId?: string;
}) => {
  const { t } = useTranslation("allFieldView");
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollRef, getCropCardRefMap, handleShowMissingDataYear, getEarliestItemIdWithMissingDataSingleView, cropCardRefCallback } =
    useMissingDataNavigation();

  const { cmcMixCropFCPO, showDrawerFromCMC, setCMCMixCropFCPO, setSelectedRecommendationItemOnTimeline, setShowDrawerFromCMC } = useCropMixComposerContext();

  const setCropCardRef = cropCardRefCallback(getCropCardRefMap);

  const fieldId = fieldDetails.id;
  const { validatePeriods } = useValidateCropPeriods();
  const { smallDesktop, largeDesktop } = useAppType();
  const { currentFarm } = useGetFarms();

  const { data: fieldCropRecommendations, isFetching: isFetchingFieldCropRecommendations } = useGetFieldCropRecommendations({
    fieldIds: [fieldId],
    enabled: !!carbonProgram,
    simulationIds: simulationId ? [simulationId] : undefined,
  });
  const { data: carbonProgramOutcomesResponse, isFetching: isFetchingCarbonProgramOutcomes } = useGetCarbonProgramOutcomes({
    fieldIds: [fieldId],
    enabled: !!carbonProgram && !!fieldId,
    simulationIds: simulationId ? [simulationId] : undefined,
  });

  const { data: practicesResponse, isFetching: isFetchingPractices } = useGetFieldPractices({ fieldId: fieldId, inView: true, simulationId });
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const fertilizerApplicationPracticesQuery = useGetPracticeOrganicFertilizerApplications({ enabled: true });
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const { mutate: updateFieldCropPractice } = useUpdateFieldCropPractice({ fieldId, farmId: currentFarm?.id });
  const isMutatingHook = !!useIsMutating();
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true);
  const [selectedCrop, setSelectedCrop] = useState<CropFCPO>();
  const [selectedFertilizers, setSelectedFertilizers] = useState<PracticeFCPO[]>([]);
  const [selectedTillages, setSelectedTillages] = useState<PracticeFCPO[]>([]);
  const [selectedItemType, setSelectedItemType] = useState<PRACTICE_TYPE>();
  const [selectedFertilizerManagements, setSelectedFertilizerManagements] = useState<FertilizerManagement[]>([]);
  const [isMainCropsCollapsed, setIsMainCropsCollapsed] = useState<boolean>(false);
  const [isSideCropsCollapsed, setIsSideCropsCollapsed] = useState<boolean>(true);
  const [timelineContainerHeight, setTimelineContainerHeight] = useState(0);
  const [missingDataItemId, setMissingDataItemId] = useState<string | null>(null);

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [items, setItems] = useState<TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>[]>([]);

  const [groups, setGroups] = useState<CustomTimelineGroup[]>([]);
  const [hiddenGroups, setHiddenGroups] = useState<GROUP_TYPE[]>([]);

  // Generate items to show on timeline
  const generateAndSetItems = (
    t: TFunction,
    fieldCropOverviewResponse: FieldPracticeResponse,
    carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse,
    fieldCropRecommendationsResponse?: CategorizedFieldCropRecommendation[]
  ) => {
    const carbonProgramResult = carbonProgramOutcomesResponse[simulationId ?? fieldId];
    const baselineYearsByPracticeType = getBaselineYearsByPracticeType(carbonProgramResult);
    const isCropMandatory = checkIfMandatoryPractice(PRACTICE_SLUG.CROPS, carbonProgramResult);
    const isTillageMandatory = checkIfMandatoryPractice(PRACTICE_SLUG.TILLAGES, carbonProgramResult);
    const isFertilizerMandatory =
      checkIfMandatoryPractice(PRACTICE_SLUG.MINERAL_FERTILIZERS, carbonProgramResult) ||
      checkIfMandatoryPractice(PRACTICE_SLUG.ORGANIC_FERTILIZERS, carbonProgramResult);
    const isSeedingMandatory = checkIfMandatoryPractice(PRACTICE_SLUG.SEEDINGS, carbonProgramResult);

    const cropItems = createCropItems(fieldCropOverviewResponse, baselineYearsByPracticeType[PRACTICE_SLUG.CROPS] || [], isCropMandatory);
    const tillageItems = createTillageItems(
      fieldCropOverviewResponse,
      cropItems,
      tillagePracticesQuery.data ?? [],
      baselineYearsByPracticeType[PRACTICE_SLUG.TILLAGES] || [],
      isTillageMandatory
    );
    const fertilizerItems = createFertilizerItems(
      t,
      fieldCropOverviewResponse,
      cropItems,
      {
        mineral: mineralFertilizerPracticesQuery.data ?? [],
        organic: organicFertilizerPracticesQuery.data ?? [],
        applications: fertilizerApplicationPracticesQuery.data ?? [],
      },
      {
        [PRACTICE_SLUG.MINERAL_FERTILIZERS]: baselineYearsByPracticeType[PRACTICE_SLUG.MINERAL_FERTILIZERS] || [],
        [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: baselineYearsByPracticeType[PRACTICE_SLUG.ORGANIC_FERTILIZERS] || [],
      },
      isFertilizerMandatory
    );
    const seedingItems = createSeedingItems(
      t,
      fieldCropOverviewResponse,
      seedingPracticesQuery.data ?? [],
      baselineYearsByPracticeType[PRACTICE_SLUG.SEEDINGS] || [],
      isSeedingMandatory
    );

    const placeholderItems = createPlaceholderItems(
      fieldCropOverviewResponse,
      baselineYearsByPracticeType[PRACTICE_SLUG.CROPS] || [],
      isEnrolled,
      carbonProgram,
      reportingYear
    );

    // create CMC placeholder cards starting from cover crops placeholder cards for years with a main crop
    const coverCropPlaceholderItems = placeholderItems.filter(
      (placeholder) =>
        placeholder.data.cropType === CROP_TYPE_FCPO.COVER &&
        cropItems.some((crop) => crop.data.cropType === CROP_TYPE_FCPO.MAIN && crop.data.year === placeholder.data.year)
    );
    const recommendationItems = createRecommendations(fieldCropRecommendationsResponse ?? [], coverCropPlaceholderItems, fieldId);

    if (practicesResponse) {
      const earliestItemIdWithMissingData = getEarliestItemIdWithMissingDataSingleView(
        carbonProgramResult,
        fieldCropOverviewResponse,
        [...cropItems, ...placeholderItems],
        fieldDetails.isInRedzone
      );

      setMissingDataItemId(earliestItemIdWithMissingData);
      setHasMissingData(earliestItemIdWithMissingData !== null);
    }

    setItems([...cropItems, ...tillageItems, ...fertilizerItems, ...seedingItems, ...recommendationItems, ...placeholderItems]);
  };

  // Custom renders for each practice type
  // eslint-disable-next-line
  const cropRender = (item: TimelineCalendarItemsProps<CropItemData>, itemContext: ItemContext, getItemProps: any): React.ReactNode => {
    const cropReferenceDate = item.data.cropType === CROP_TYPE_FCPO.MAIN ? item.end_time : item.start_time;
    const cropIsWithinReportingPeriod = timeIsWithinReportingPeriod(cropReferenceDate.valueOf(), reportingYear, carbonProgram);

    return (
      <div
        {...getItemProps(item.itemProps)}
        ref={(node) => {
          setCropCardRef(node, item.group, item.id as string);
        }}
      >
        <CropRotationCard
          dataTestId={`${item.practiceType}-${item.id}`}
          cropId={item.cropItemId ?? ""}
          cropName={item.data.cropName}
          hasResidue={!!item.data.hasResidue}
          cropType={item.data.cropType}
          payout={item.data.payout}
          currency={carbonProgram?.currency}
          // we only want to see the selected card state when the drawer is open or when the practices are loading, when it closes, we unselect the card
          isSelected={itemContext.selected && (showDrawer || isFetchingPractices || isMutatingHook)}
          isBaseline={item.data.isBaseline}
          isMandatory={item.data.isMandatory}
          isConfirmed={!!item.data.isConfirmed}
          isEmptyCard={item.isPlaceholder}
          isReportingYear={cropIsWithinReportingPeriod}
          simulationId={simulationId}
          fieldSize={fieldDetails.size}
          cropMixId={item.data.cropMixId}
        />
      </div>
    );
  };

  // eslint-disable-next-line
  const sideCropRecommendationRender = (item: TimelineCalendarItemsProps<CropItemData>, getItemProps: any): React.ReactNode => (
    <div {...getItemProps(item.itemProps)}>
      <CropRotationCard
        dataTestId={`${item.practiceType}-${item.id}`}
        cropId=""
        cropName={item.data.cropName}
        hasResidue={!!item.data.hasResidue}
        cropType={item.data.cropType}
        payout={item.data.payout}
        currency={carbonProgram?.currency}
        year={item.data.year}
        isEmptyCard={true}
        recommendations={item.data.recommendations}
        simulationId={simulationId}
        fieldSize={fieldDetails.size}
      />
    </div>
  );

  // eslint-disable-next-line
  const tillageRender = (item: TimelineCalendarItemsProps<TillageItemData>, getItemProps: any): React.ReactNode => (
    <div {...getItemProps(item.itemProps)}>
      <PracticeCard
        currency={carbonProgram?.currency}
        valuesToDisplay={item.data?.tillageValues}
        startTime={item.start_time}
        endTime={item.end_time}
        type={PRACTICE_TYPE.TILLAGE}
      />
    </div>
  );

  // eslint-disable-next-line
  const fertilizerRender = (item: TimelineCalendarItemsProps<FertilizerItemData>, getItemProps: any): React.ReactNode => (
    <div {...getItemProps(item.itemProps)}>
      <PracticeCard
        currency={carbonProgram?.currency}
        valuesToDisplay={item.data?.fertilizerValues}
        startTime={item.start_time}
        endTime={item.end_time}
        type={PRACTICE_TYPE.FERTILIZER}
      />
    </div>
  );

  // eslint-disable-next-line
  const seedingRender = (item: TimelineCalendarItemsProps<SeedingItemData>, getItemProps: any): React.ReactNode => (
    <div {...getItemProps(item.itemProps)}>
      <PracticeCard
        valuesToDisplay={item.data?.seedingValue ? [item.data?.seedingValue] : []}
        startTime={item.start_time}
        endTime={item.end_time}
        type={PRACTICE_TYPE.SEEDING}
      />
    </div>
  );

  // eslint-disable-next-line
  const itemRenderer = ({ item, itemContext, getItemProps }: { item: any; itemContext: ItemContext; getItemProps: any }): React.ReactNode => {
    switch (item.practiceType) {
      case PRACTICE_TYPE.MAIN_CROP:
      case PRACTICE_TYPE.COVER_CROP:
      case PRACTICE_TYPE.UNDERSOWING_CROP:
        return cropRender(item, itemContext, getItemProps);
      case PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION:
        return sideCropRecommendationRender(item, getItemProps);
      case PRACTICE_TYPE.FERTILIZER:
        return fertilizerRender(item, getItemProps);
      case PRACTICE_TYPE.TILLAGE:
        return tillageRender(item, getItemProps);
      case PRACTICE_TYPE.SEEDING:
        return seedingRender(item, getItemProps);
      default:
    }
  };

  // eslint-disable-next-line
  const groupRenderer = ({ group }: ReactCalendarGroupRendererProps<CustomTimelineGroup>) => {
    return (
      <div className={`group ${group.isLastOfGroup ? "" : "no-border"}`}>
        {group.id === GROUP_TYPE.MAIN_CROP && (
          <div className="pointer flex-center" onClick={() => setIsMainCropsCollapsed((prevState) => !prevState)}>
            {isMainCropsCollapsed ? <Icon iconName="tabler/chevron-down" size="m" /> : <Icon iconName="tabler/chevron-up" size="m" />}
          </div>
        )}
        {group.id === GROUP_TYPE.SIDE_CROP && (
          <div className="pointer flex-center" onClick={() => setIsSideCropsCollapsed((prevState) => !prevState)}>
            {isSideCropsCollapsed ? <Icon iconName="tabler/chevron-down" size="m" /> : <Icon iconName="tabler/chevron-up" size="m" />}
          </div>
        )}
      </div>
    );
  };

  // TODO:(Alp) Write tests after fixing import issue of react-calendar-timeline in jest tests.
  const updateItem = (item: TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>, updateType: UPDATE_TYPE) => {
    if (!item.cropItemId) return;
    if (!practicesResponse) return;
    // Filter out placeholder
    if (item.isPlaceholder) return;

    const requestBody: CropPracticesRequestBody | null = createCropPracticesRequestBody(item, items, practicesResponse, updateType);
    if (!requestBody) return;

    const fcpoCropToUpdate = practicesResponse.crops.find((crop) => crop.id === item.id);
    if (!fcpoCropToUpdate) return;

    const validationErrors = validatePeriods(
      {
        ...fcpoCropToUpdate,
        seedingStartDate: requestBody.crop.seedingStartDate,
        harvestEndDate: requestBody.crop.harvestEndDate,
      },
      practicesResponse.crops
    );
    if (validationErrors.length) {
      message({ content: validationErrors[0].message, icon: "error" });
      return;
    }

    updateFieldCropPractice({
      requestBody,
      cropId: item.id.toString(), // This is the id of fieldPracticeResponse.crop.id
    });
  };

  // TODO:(Alp) Write tests after fixing import issue of react-calendar-timeline in jest tests.
  // @ts-expect-error Use right types
  const handleItemResize = (itemId, time, edge) => {
    const itemToUpdate = items.find((item) => item.id === itemId) as TimelineCalendarItemsProps<CropItemData>;
    if (!itemToUpdate) return;
    if (itemToUpdate.data.isConfirmed) {
      message({ content: t("cropForm.errorMessages.notAllowedEdit"), icon: "error" });
      return;
    }
    const newItem = {
      ...itemToUpdate,
      start_time: edge === "left" ? moment(time) : itemToUpdate.start_time,
      end_time: edge === "left" ? itemToUpdate.end_time : moment(time),
    };
    const isBeforeLastConfirmedCrop = isDateValidConsideringLastConfirmedCrop(
      newItem.start_time,
      newItem.end_time,
      practicesResponse?.crops,
      newItem.data.cropType,
      newItem.data.year
    );
    if (!isBeforeLastConfirmedCrop) {
      message({ content: t("cropForm.errorMessages.notAllowedToMoveBeforeConfirmationDate"), icon: "error" });
      return;
    }
    const updateType: UPDATE_TYPE = edge === "left" ? UPDATE_TYPE.START : UPDATE_TYPE.END;
    updateItem(newItem, updateType);
  };

  // TODO:(Alp) Write tests after fixing import issue of react-calendar-timeline in jest tests.
  // @ts-expect-error Use right types
  const handleItemMove = (itemId, dragTime) => {
    const itemToUpdate = items.find((item) => item.id === itemId) as TimelineCalendarItemsProps<CropItemData>;
    if (!itemToUpdate) return;
    if (itemToUpdate.data.isConfirmed) {
      message({ content: t("cropForm.errorMessages.notAllowedEdit"), icon: "error" });
      return;
    }
    const dragTimeInMoment = moment(dragTime);
    const end = moment(itemToUpdate.end_time).diff(moment(itemToUpdate.start_time), "milliseconds") + dragTime;
    const newItem = {
      ...itemToUpdate,
      start_time: dragTimeInMoment,
      end_time: moment(end),
    };
    const isBeforeLastConfirmedCrop = isDateValidConsideringLastConfirmedCrop(
      newItem.start_time,
      newItem.end_time,
      practicesResponse?.crops,
      newItem.data.cropType,
      newItem.data.year
    );
    if (!isBeforeLastConfirmedCrop) {
      message({ content: t("cropForm.errorMessages.notAllowedToMoveBeforeConfirmationDate"), icon: "error" });
      return;
    }
    updateItem(newItem, UPDATE_TYPE.MOVE);
  };

  const isCMCIconClick = (target: HTMLElement | null) => target?.classList.contains("cmc-plant-icon");

  // TODO:(Alp) Write tests after fixing import issue of react-calendar-timeline in jest tests.
  const onItemClicked = (itemId: Id, event?: React.SyntheticEvent) => {
    if (!practicesResponse) return;

    const clickedItem = items.find((item) => item.id === itemId);
    if (!clickedItem) return;
    setSelectedRecommendationItemOnTimeline?.(clickedItem as TimelineCalendarItemsProps<CropItemData>);

    // Extract the base path before /cmc to avoid double /cmc paths
    const basePath = location.pathname.includes("/cmc") ? location.pathname.split("/cmc")[0].replace(/\/$/, "") : location.pathname.replace(/\/$/, "");

    if (clickedItem.isPlaceholder || clickedItem.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION) {
      const startTime = clickedItem.start_time.toISOString();
      const endTime = clickedItem.end_time.toISOString();
      setSelectedItemType(clickedItem.practiceType);
      setSelectedCrop(NEW_CROP((clickedItem.data as CropItemData).cropType, startTime, endTime));
      setSelectedFertilizers([]);
      setSelectedTillages([]);
      setSelectedFertilizerManagements([]);

      if (clickedItem.practiceType !== PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION) setShowDrawer(true);
      else if ((clickedItem.data as CropItemData).cropType === CROP_TYPE_FCPO.COVER) {
        setSelectedRecommendationItemOnTimeline?.(clickedItem as TimelineCalendarItemsProps<CropItemData>);
        navigate(`${basePath}/cmc${location.search}`);
      }
      return;
    }

    const cropItemId = clickedItem?.cropItemId;
    if (!cropItemId) return;

    if (isCMCIconClick(event?.target as HTMLElement) || isCMCIconClick((event?.target as HTMLElement)?.parentElement)) {
      setShowDrawer(false);
      setSelectedRecommendationItemOnTimeline?.(clickedItem as TimelineCalendarItemsProps<CropItemData>);
      navigate(`${basePath}/cmc/${(clickedItem.data as CropItemData).cropMixId}${location.search}`);
      return;
    }

    const crop = practicesResponse.crops.find((crop) => crop.id === cropItemId);
    if (!crop) return;

    const fertilizers = practicesResponse.practices.filter(
      (practice) =>
        practice.cropId === cropItemId && (practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS)
    );
    const tillage = practicesResponse.practices.filter((practice) => practice.cropId === cropItemId && practice.type === PRACTICE_SLUG.TILLAGES);

    setCMCMixCropFCPO({} as CropFCPO);
    setSelectedItemType(clickedItem.practiceType);
    setSelectedCrop(crop);
    setSelectedFertilizers(fertilizers);
    setSelectedTillages(tillage);
    // Fertilizer managements are just field/year specific, not tied to a crop
    setSelectedFertilizerManagements(practicesResponse.fertilizerManagements);
    setShowDrawer(true);
  };

  const filterOutPlaceholderOverlappingWithRecommendations = (
    item: TimelineCalendarItemsProps<CropItemData | TillageItemData | FertilizerItemData | SeedingItemData>
  ) => {
    const recommendations = items.filter((item) => item.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION);
    if (!item.isPlaceholder) return true;
    if (item.practiceType === PRACTICE_TYPE.MAIN_CROP) return true;

    const overlappingPlaceholder = recommendations.find((recommendation) =>
      doRangesOverlap(recommendation.start_time, recommendation.end_time, item.start_time, item.end_time)
    );
    return !overlappingPlaceholder;
  };

  const field: FieldListField = {
    id: fieldDetails.id,
    name: fieldDetails.name,
    size: fieldDetails.size,
    isInRedzone: fieldDetails.isInRedzone,
    redzones: [],
    location: { lat: fieldDetails.location?.lat, lng: fieldDetails.location?.lng },
    regionId: fieldDetails.regionId,
  };

  useEffect(
    () => {
      if (
        tillagePracticesQuery.isLoading ||
        seedingPracticesQuery.isLoading ||
        mineralFertilizerPracticesQuery.isLoading ||
        organicFertilizerPracticesQuery.isLoading ||
        fertilizerApplicationPracticesQuery.isLoading ||
        isFetchingPractices ||
        isFetchingCarbonProgramOutcomes ||
        !practicesResponse ||
        !carbonProgramOutcomesResponse
      )
        return;
      generateAndSetItems(t, practicesResponse, carbonProgramOutcomesResponse, fieldCropRecommendations?.[simulationId ?? fieldId]);
      const createdGroups = createGroups(practicesResponse);
      setGroups(createdGroups);
      setIsLoadingPage(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      practicesResponse,
      mineralFertilizerPracticesQuery.isLoading,
      organicFertilizerPracticesQuery.isLoading,
      fertilizerApplicationPracticesQuery.isLoading,
      tillagePracticesQuery.isLoading,
      seedingPracticesQuery.isLoading,
      isFetchingPractices,
      fieldCropRecommendations,
      isFetchingFieldCropRecommendations,
      isFetchingCarbonProgramOutcomes,
      carbonProgramOutcomesResponse,
    ]
  );

  useEffect(() => {
    const mainCropGroupsToHide = [GROUP_TYPE.MAIN_CROP_SEEDING, GROUP_TYPE.MAIN_CROP_TILLAGE, GROUP_TYPE.MAIN_CROP_FERTILIZER];
    const sideCropGroupsToHide = [GROUP_TYPE.SIDE_CROP_SEEDING, GROUP_TYPE.SIDE_CROP_TILLAGE, GROUP_TYPE.SIDE_CROP_FERTILIZER];

    const groups: GROUP_TYPE[] = [];

    if (isMainCropsCollapsed) {
      groups.push(...mainCropGroupsToHide);
    }
    if (!isMainCropsCollapsed) {
      const result = hiddenGroups.filter((item) => !mainCropGroupsToHide.includes(item));
      setHiddenGroups(result);
    }
    if (isSideCropsCollapsed) {
      groups.push(...sideCropGroupsToHide);
    }
    if (!isSideCropsCollapsed) {
      const result = hiddenGroups.filter((item) => !sideCropGroupsToHide.includes(item));
      setHiddenGroups(result);
    }

    setHiddenGroups(groups);
  }, [isMainCropsCollapsed, isSideCropsCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pageHeaderSelectors = [".header-desktop.container", ".field-timeline-header.header", ".rct-header-root.timeline-calendar-header"];

    const calculateTimelineContainerHeight = () => {
      const timelineContainerHeight = pageHeaderSelectors.reduce((timelineContainerHeight, selector) => {
        const element = document.querySelector(selector);
        if (!element) return timelineContainerHeight;
        return timelineContainerHeight - element.getBoundingClientRect().height;
      }, window.innerHeight - 1); // we need to account for the timeline container 1px border

      setTimelineContainerHeight(timelineContainerHeight);
    };

    // Observe changes in elements
    const observers: ResizeObserver[] = [];
    pageHeaderSelectors.forEach((selector) => {
      const element = document.querySelector(selector);
      if (element) {
        const observer = new ResizeObserver(calculateTimelineContainerHeight);
        observer.observe(element);
        observers.push(observer);
      }
    });

    // MutationObserver to detect when elements appear
    const mutationObserver = new MutationObserver(() => {
      calculateTimelineContainerHeight();
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    calculateTimelineContainerHeight(); // Initial calculation

    return () => {
      observers.forEach((observer) => observer.disconnect());
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (navigateToMissingData) {
      if (missingDataItemId) handleShowMissingDataYear(GROUP_TYPE.MAIN_CROP, missingDataItemId);

      setNavigateToMissingData(false);
    }
  }, [navigateToMissingData, missingDataItemId, handleShowMissingDataYear, setNavigateToMissingData]);

  useEffect(() => {
    if (!cmcMixCropFCPO.cropId) return;
    setSelectedCrop(cmcMixCropFCPO);
  }, [cmcMixCropFCPO.cropId]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO: (Alp) We should be able to use Moment instead of number and custom item and group properties
  return (
    <>
      {isLoadingPage && practicesResponse ? (
        <PageSpin />
      ) : (
        <TimelineCalendar
          className="timeline-calendar-field-details"
          groups={getGroupsToShow(groups, hiddenGroups, timelineContainerHeight)}
          // @ts-expect-error nope should be a date type and not a number
          items={
            showRecommendations
              ? items.filter(filterOutPlaceholderOverlappingWithRecommendations)
              : items.filter((item) => item.practiceType !== PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION)
          }
          itemRenderer={itemRenderer}
          onItemResize={handleItemResize}
          onItemMove={handleItemMove}
          groupRenderer={groupRenderer}
          highlightCurrentYear={!simulationId}
          isSimulationView={!!simulationId}
          tagText={!simulationId ? t("reportingYear") : ""}
          dragSnap={dayInMilliseconds}
          canChangeGroup={false}
          onItemClick={(itemId: Id, e: React.SyntheticEvent) => onItemClicked(itemId, e)}
          onItemSelect={(itemId: Id, e: React.SyntheticEvent) => onItemClicked(itemId, e)}
          // TODO: re-add drag and drop and resizing
          // canMove={!isUpdatingFieldCropPractice}
          // canResize={!isUpdatingFieldCropPractice}
          canMove={false}
          canResize={false}
          screenSize={largeDesktop ? "large" : smallDesktop ? "small" : "medium"}
          scrollRef={(el) => (scrollRef.current = el)}
          buffer={8}
          reportingYear={reportingYear}
        />
      )}

      {practicesResponse &&
        (showDrawer && selectedCrop ? (
          <AddEditCropDrawer
            availablePracticeBundles={practicesResponse.availablePracticeBundles}
            allCrops={practicesResponse.crops}
            existingCrop={selectedCrop}
            year={selectedCrop?.year ?? moment().year()}
            existingFertilizers={selectedFertilizers}
            existingTillages={selectedTillages}
            field={field}
            setShowDrawer={setShowDrawer}
            showDrawer={showDrawer}
            type={selectedItemType}
            fertilizerManagements={selectedFertilizerManagements}
            simulationId={simulationId}
            viewType={simulationId ? VIEW_TYPE.SIMULATION : VIEW_TYPE.SINGLE_FIELD}
            farmId={currentFarm?.id}
            carbonProgram={currentFarm?.carbonProgram}
          />
        ) : showDrawerFromCMC && cmcMixCropFCPO.cropId ? (
          <AddEditCropDrawer
            availablePracticeBundles={practicesResponse.availablePracticeBundles}
            allCrops={practicesResponse.crops}
            existingCrop={cmcMixCropFCPO}
            year={cmcMixCropFCPO?.year ?? moment().year()}
            existingFertilizers={selectedFertilizers}
            existingTillages={selectedTillages}
            field={field}
            setShowDrawer={setShowDrawerFromCMC}
            showDrawer={showDrawerFromCMC}
            type={PRACTICE_TYPE.COVER_CROP}
            fertilizerManagements={selectedFertilizerManagements}
            simulationId={simulationId}
            viewType={simulationId ? VIEW_TYPE.SIMULATION : VIEW_TYPE.SINGLE_FIELD}
            farmId={currentFarm?.id}
            carbonProgram={currentFarm?.carbonProgram}
          />
        ) : null)}
    </>
  );
};
